import { LAUNCH_STARTERS, STARTER_BIOMES } from './types.js';
import type {
  AnimalKind, Biome, Difficulty, GrowthStage, SeedKind, SharedBiome, StarterBiome, TreeSpecies,
} from './types.js';

/**
 * Every tunable number. The server stores these as versioned records, and each
 * ledger event notes the version it was paid under, so balancing never needs a
 * code release and never changes past payouts.
 */
export interface Rules {
  version: number;
  rewards: Record<Difficulty, { coins: number; xp: number; seedChance: number }>;
  seedFor: Record<Difficulty, SeedKind>;
  /** Paid each time a daily's streak reaches a multiple of `every`. */
  weeklyBonus: { every: number; coins: number; seed: boolean };
  /** XP to go from level L to L + 1 is `base + perLevel × L`. */
  levelCurve: { base: number; perLevel: number };
  /** Completions for small, medium, large and mega. */
  stageCounts: Record<Difficulty, [number, number, number, number]>;
  species: Record<Difficulty, TreeSpecies>;
  bloomDays: Record<Difficulty, number>;
  pickCoins: Record<Difficulty, number>;
  /** Completions in a new streak that refill sparse leaves. */
  leafRecovery: number;
  weedFadeDays: number;
  maxWeedsPerHabit: number;
  /** The sacred grove also opens with the first mega tree. */
  biomeUnlockLevels: Record<SharedBiome, number>;
  /**
   * Biomes released so far. Thicket launches with three starters (meadow, pine ridge and
   * canyon) and no shared biomes; adding a biome here (in a new rules version) releases it,
   * and players who already qualify get it then. A starter not listed can't be chosen, and a
   * shared biome not listed never unlocks.
   */
  releasedBiomes: Biome[];
  animals: Record<AnimalKind, { price: number; minTreeStage: GrowthStage | null }>;
  bedPlots: number;
  labelMax: number;
}

export const DEFAULT_RULES: Rules = {
  version: 1,
  rewards: {
    trivial: { coins: 1, xp: 5, seedChance: 0.1 },
    easy: { coins: 2, xp: 10, seedChance: 0.2 },
    medium: { coins: 3, xp: 15, seedChance: 0.3 },
    hard: { coins: 5, xp: 25, seedChance: 0.5 },
  },
  seedFor: { trivial: 'clover', easy: 'daisy', medium: 'tulip', hard: 'sunflower' },
  weeklyBonus: { every: 7, coins: 10, seed: true },
  levelCurve: { base: 50, perLevel: 10 },
  stageCounts: {
    trivial: [10, 30, 75, 200],
    easy: [7, 21, 50, 150],
    medium: [5, 15, 35, 100],
    hard: [4, 12, 25, 75],
  },
  species: { trivial: 'birch', easy: 'maple', medium: 'oak', hard: 'pine' },
  bloomDays: { trivial: 7, easy: 7, medium: 10, hard: 14 },
  pickCoins: { trivial: 1, easy: 2, medium: 3, hard: 5 },
  leafRecovery: 3,
  weedFadeDays: 3,
  maxWeedsPerHabit: 3,
  biomeUnlockLevels: { woodlandLake: 25, coast: 50, mountainSummit: 75, sacredGrove: 100 },
  releasedBiomes: [...LAUNCH_STARTERS],
  animals: {
    // Decided Oct 11, 2026: the bumblebee is the one companion a first day's coins can buy; the
    // rest take some work. A normal day pays about 15–20 coins; tree companions also wait on a tree.
    songbird: { price: 50, minTreeStage: 'small' },
    squirrel: { price: 120, minTreeStage: 'medium' },
    owl: { price: 200, minTreeStage: 'large' },
    // Bed companions live around the raised bed or flower cart (minTreeStage null).
    bumblebee: { price: 15, minTreeStage: null },
    toad: { price: 60, minTreeStage: null },
    butterfly: { price: 90, minTreeStage: null },
  },
  bedPlots: 18,
  labelMax: 10,
};

/** Whether a biome is out yet. Rules saved before biomes were released one at a time count as the launch starters only. */
export const isReleased = (rules: Rules, biome: Biome): boolean => (rules.releasedBiomes ?? LAUNCH_STARTERS).includes(biome);

/** The starters a new player may pick right now. */
export const releasedStarters = (rules: Rules): StarterBiome[] => STARTER_BIOMES.filter((b) => isReleased(rules, b));

export function xpForNextLevel(level: number, rules: Rules): number {
  return rules.levelCurve.base + rules.levelCurve.perLevel * level;
}

export function levelProgress(xp: number, rules: Rules): { level: number; into: number; needed: number } {
  let level = 1;
  let left = xp;
  while (left >= xpForNextLevel(level, rules)) {
    left -= xpForNextLevel(level, rules);
    level++;
  }
  return { level, into: left, needed: xpForNextLevel(level, rules) };
}

export function levelOf(xp: number, rules: Rules): number {
  return levelProgress(xp, rules).level;
}

export function growthStage(count: number, pace: Difficulty, rules: Rules): GrowthStage {
  const [small, medium, large, mega] = rules.stageCounts[pace];
  if (count >= mega) return 'mega';
  if (count >= large) return 'large';
  if (count >= medium) return 'medium';
  if (count >= small) return 'small';
  return 'seed';
}

export function stageRank(stage: GrowthStage): number {
  return ['seed', 'small', 'medium', 'large', 'mega'].indexOf(stage);
}

export const ANIMAL_NAMES: Record<AnimalKind, string> = {
  songbird: 'Songbird', squirrel: 'Squirrel', owl: 'Owl', bumblebee: 'Bumblebee', toad: 'Garden toad', butterfly: 'Butterfly',
};

export const PET_NAMES = [
  'Pip', 'Juniper', 'Moss', 'Hazel', 'Clove', 'Biscuit', 'Acorn',
  'Fern', 'Sage', 'Pepper', 'Maple', 'Olive', 'Thistle', 'Bramble',
] as const;
